/**
 * API client for points system endpoints.
 */
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Points API client
 */
export const pointsAPI = {
  /**
   * Get current student's points
   */
  getPoints: async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl('/users/points/'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch points' }));
      throw new Error(error.error || error.message || 'Failed to fetch points');
    }

    return response.json();
  },

  /**
   * Award points for an activity
   * @param {Object} data - { amount_xp, amount_gems, reason, related_object_type, related_object_id }
   */
  awardPoints: async (data) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl('/users/points/activity/'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to award points' }));
      throw new Error(error.error || error.message || 'Failed to award points');
    }

    return response.json();
  },

  /**
   * Claim daily login bonus
   */
  claimDailyLogin: async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl('/users/points/daily-login/'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to claim daily login bonus' }));
      throw new Error(error.error || error.message || 'Failed to claim daily login bonus');
    }

    return response.json();
  },

  /**
   * Buy heart refill with gems (5 gems = 1 heart)
   */
  buyHeartRefill: async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl('/users/points/heart-refill/'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to buy heart refill' }));
      throw new Error(error.error || error.message || 'Failed to buy heart refill');
    }

    return response.json();
  },

  /**
   * Get points transaction history
   * @param {Object} params - Query parameters (optional)
   */
  getHistory: async (params = {}) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const queryParams = new URLSearchParams(params).toString();
    const url = apiUrl('/users/points/history/') + (queryParams ? `?${queryParams}` : '');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch history' }));
      throw new Error(error.error || error.message || 'Failed to fetch history');
    }

    return response.json();
  },
};
